import { observer } from "mobx-react-lite"
import { FC, useContext, useEffect, useLayoutEffect } from "react"
import { Context } from "../.."
import { XIcons } from "../../app/libs/x-ui-core/components"
import { isSplashPath, prevent } from "../../utils"
import { useLocation } from "react-router-dom"

const GlobalFader: FC = () => {
  const { store } = useContext(Context)
  const location = useLocation()
  const onSplash = isSplashPath(location.pathname)

  // Сразу показываем заглушку до отрисовки кадра, чтобы не мелькал пустой экран или недогруженный сайт.
  useLayoutEffect(() => {
    if (!onSplash) return
    store.setIsFaderVisible(true)
    store.setIsFaderShown(true)
    store.setIsFaderLogoVisible(true)
    // Каждый заход на splash-страницу заново ждёт готовности контента.
    store.setIsStoreContentReady(false)
    // Снимаем статическую заглушку из index.html — React-фейдер теперь покрывает ту же область.
    document.getElementById("initial-splash")?.remove()
    return () => {
      store.setIsFaderVisible(false)
      store.setIsFaderShown(false)
      store.setIsFaderLogoVisible(false)
    }
  }, [onSplash, store])

  // Как только настройки подтянулись — запускаем анимации контента под заглушкой.
  useEffect(() => {
    if (!store.isSettingsInited || !onSplash) return
    store.setAnimationToggle(true)
  }, [store.isSettingsInited, onSplash, store])

  // Основной путь: ждём, пока критические данные (профиль/бонусы/заказы) реально дорендерятся.
  useEffect(() => {
    if (!onSplash) return
    if (!store.isSettingsInited) return
    if (!store.isStoreContentReady) return

    let t2: ReturnType<typeof setTimeout> | undefined
    const t1 = setTimeout(() => {
      store.setIsFaderShown(false)
      t2 = setTimeout(() => {
        store.setIsFaderVisible(false)
      }, 300)
    }, 300)

    return () => {
      clearTimeout(t1)
      if (t2) clearTimeout(t2)
    }
  }, [store.isSettingsInited, store.isStoreContentReady, onSplash, store])

  // Жёсткий fallback на 15 секунд — если данные не пришли (ошибка сети/API), всё равно убираем заглушку,
  // чтобы пользователь не застрял на сером экране.
  useEffect(() => {
    if (!onSplash) return

    const t1 = setTimeout(() => {
      store.setIsFaderShown(false)
      setTimeout(() => {
        store.setIsFaderVisible(false)
      }, 300)
    }, 3000)

    return () => {
      clearTimeout(t1)
    }
  }, [onSplash, store])

  // Закрыть вручную можно только после загрузки настроек — иначе вылез бы «сырой» интерфейс.
  const dismiss = () => {
    if (!store.isSettingsInited) return
    store.setIsFaderShown(false)
    setTimeout(() => {
      store.setIsFaderVisible(false)
    }, 300)
  }

  return (
    <>
      <div
        className="fader"
        style={{
          display: store.isFaderVisible ? "flex" : "none",
          opacity: store.isFaderShown ? "1" : "0",
          backgroundColor: "rgb(238, 238, 238)",
        }}
        onClick={() => dismiss()}
      >
        {/* Крестик только после готовности приложения — до этого заглушку не убираем по клику. */}
        {store.isSettingsInited ? (
          <span
            onClick={(ev) => {
              prevent(ev)
              dismiss()
            }}
          >
            <XIcons name="r times" />
          </span>
        ) : null}
        <div>
          {/* Картинка заглушки: высокий приоритет и sync-декод, чтобы быстрее показать первый кадр. */}
          <img
            fetchPriority="high"
            decoding="sync"
            alt=""
            style={{
              opacity: store.isFaderLogoVisible ? "1" : "0",
              maxHeight: "100%",
            }}
            src="/assets/images/statuses/story_new.webp"
          />
        </div>
      </div>
    </>
  )
}

export default observer(GlobalFader)
